// src/presentation/pages/ClinicalExamination/sections/CerebrovascularHistory.tsx
import React from "react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

interface CerebrovascularHistoryProps {
  hasHadStroke: boolean | null;
  strokeYear: string;
  hasHadTIA: boolean | null;
  tiaYear: string;
  hasMultipleStrokes: boolean | null;
  multipleStrokesYear: string;
  onStrokeChange: (value: boolean) => void;
  onStrokeYearChange: (value: string) => void;
  onTIAChange: (value: boolean) => void;
  onTIAYearChange: (value: string) => void;
  onMultipleStrokesChange: (value: boolean) => void;
  onMultipleStrokesYearChange: (value: string) => void;
}

export const CerebrovascularHistory: React.FC<CerebrovascularHistoryProps> = ({
  hasHadStroke,
  strokeYear,
  hasHadTIA,
  tiaYear,
  hasMultipleStrokes,
  multipleStrokesYear,
  onStrokeChange,
  onStrokeYearChange,
  onTIAChange,
  onTIAYearChange,
  onMultipleStrokesChange,
  onMultipleStrokesYearChange,
}) => {
  const { t } = useTranslation("patients");
  return (
    <div className="border-gray-200">
      <CardHeader className="pb-3 !px-0">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.cerebrovascularHistory.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className=" space-y-4 !px-0">
        {/* Stroke Question */}
        <div>
          <p className="text-sm text-gray-700 mb-1">
            {t("clinicalSections.cerebrovascularHistory.strokeQuestion")}
          </p>
          <RadioGroup
            value={hasHadStroke?.toString()}
            onValueChange={(val) => onStrokeChange(val === "true")}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="stroke-yes" />
              <Label
                htmlFor="stroke-yes"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.yes")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="stroke-no" />
              <Label
                htmlFor="stroke-no"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.no")}
              </Label>
            </div>
          </RadioGroup>
          {hasHadStroke === true && (
            <div className="mt-3     border-primary/30">
              <Label htmlFor="stroke-year" className="text-sm text-[#5F646F]">
                {t("clinicalSections.cerebrovascularHistory.yearLabel")}
              </Label>
              <Input
                id="stroke-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={strokeYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onStrokeYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          )}
        </div>

        {/* TIA Question */}
        <div>
          <p className="text-sm text-gray-700 mb-1">
            {t("clinicalSections.cerebrovascularHistory.tiaQuestion")}
          </p>
          <RadioGroup
            value={hasHadTIA?.toString()}
            onValueChange={(val) => onTIAChange(val === "true")}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="tia-yes" />
              <Label
                htmlFor="tia-yes"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.yes")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="tia-no" />
              <Label
                htmlFor="tia-no"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.no")}
              </Label>
            </div>
          </RadioGroup>
          {hasHadTIA === true && (
            <div className="mt-3     border-primary/30">
              <Label htmlFor="tia-year" className="text-sm text-[#5F646F]">
                {t("clinicalSections.cerebrovascularHistory.yearLabel")}
              </Label>
              <Input
                id="tia-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={tiaYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onTIAYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          )}
        </div>

        {/* Multiple Stroke Events */}
        <div>
          <p className="text-sm text-gray-700 mb-1">
            {t("clinicalSections.cerebrovascularHistory.multipleStrokeQuestion")}
          </p>
          <RadioGroup
            value={hasMultipleStrokes?.toString()}
            onValueChange={(val) => onMultipleStrokesChange(val === "true")}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="true" id="multiple-yes" />
              <Label
                htmlFor="multiple-yes"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.yes")}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="false" id="multiple-no" />
              <Label
                htmlFor="multiple-no"
                className="text-[#1B1D21] cursor-pointer text-sm font-normal"
              >
                {t("clinicalSections.no")}
              </Label>
            </div>
          </RadioGroup>
          {hasMultipleStrokes === true && (
            <div className="mt-3   border-primary/30">
              <Label
                htmlFor="multiple-strokes-year"
                className="text-[#5F646F] text-sm"
              >
                {t("clinicalSections.cerebrovascularHistory.yearLabel")}
              </Label>
              <Input
                id="multiple-strokes-year"
                type="text"
                inputMode="numeric"
                placeholder="YYYY"
                value={multipleStrokesYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                  onMultipleStrokesYearChange(v);
                }}
                className="mt-1 w-40"
              />
            </div>
          )}
        </div>
      </CardContent>
    </div>
  );
};
